import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import type {
  OrgHierarchy,
  OrgHierarchyListResponse,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || 'An error occurred';
    }
  }
  return 'An error occurred';
};

export const useListOrgHierarchies = () => {
  return useQuery({
    queryKey: ['orgHierarchies'],
    queryFn: async () => {
      const { data } = await apiClient.get<OrgHierarchyListResponse>(
        '/org/hierarchies'
      );
      return data.items || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useGetOrgHierarchy = (hierarchyId: string | null) => {
  return useQuery({
    queryKey: ['orgHierarchies', hierarchyId],
    queryFn: async () => {
      if (!hierarchyId) return null;
      const { data } = await apiClient.get<OrgHierarchy>(
        `/org/hierarchies/${hierarchyId}`
      );
      return data;
    },
    enabled: !!hierarchyId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateOrgHierarchy = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
      const { data } = await apiClient.post<OrgHierarchy>(
        '/org/hierarchies',
        payload
      );
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: `Hierarchy "${data.name}" created successfully`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['orgHierarchies'] });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to create hierarchy',
        type: 'error',
      });
    },
  });
};
